import { Typography } from "antd-v5";
import AppLoader from "components/AppLoader";
import message from "components/CustomMessage";
import { SharablePopUp } from "components/ShareIt";
import Vocabulary from "components/Vocabulary";
import WarningPopup from "components/WarningPopup";
import { useFeatureFlag } from "contexts/FeatureFlagContext";
import { useFindingBucket } from "contexts/FindingBucketContext";
import { useGlobalFilterContext } from "contexts/GlobalFilterContext";
import useAQLFields from "hooks/useAQLFields";
import useDetectFilterChange from "hooks/useDetectFilterChange";
import { EntityNavigatorParams } from "hooks/useEntityNavigator";
import useFindingColumns from "hooks/useFindingColumns";
import useModalRenderer from "hooks/useModalRenderer";
import usePaginationUrlControls from "hooks/usePaginationUrlControls";
import useTableCustomizationHelper, {
  DummyCustomizationHelper,
  TableCustomizationHelper,
} from "hooks/useTableCustomizationHelper";
import useUpdateEffect from "hooks/useUpdateEffect";
import _, { forEach, isUndefined, map, noop, omit, some } from "lodash";
import React, {
  MouseEvent,
  MutableRefObject,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useHistory, useLocation } from "react-router-dom";
// @ts-expect-error - Could not find a declaration file for module 'react-triggerable-effect'.
import useTriggerableEffect from "react-triggerable-effect";
import FindingAPI from "restAPI/FindingAPI";
import ToolsIntegrationsAPI from "restAPI/ToolsIntegrationAPI";
import { AQLMetadataDTO } from "types/api/aqlMetadataDto";
import { ExportField } from "types/api/exportField";
import { FindingFiltersRequest } from "types/api/findingFiltersRequest";
import { FindingFiltersRequestDto } from "types/api/findingFiltersRequestDto";
import { FindingSummary } from "types/api/findingSummary";
import { ListFindingResponse } from "types/api/listFindingResponse";
import { NameIdPair } from "types/api/nameIdPair";
import { SortOrder as FindingListSortOrder } from "types/api/sortOrder";
import { AQLOperatorSupportMap } from "types/aqlOperatorSupportMap";
import {
  DummyPaginationControlls,
  FilterInfo,
  FilterOptionInfo,
  PaginationControls,
  SortInfo,
} from "types/pagination";
import {
  ArmorcodeCustomField,
  CustomFindingColumn,
  DerivedAttribute,
} from "types/tenantConfig";
import { getAqlFilterKey } from "utils/aqlFilterUtils";
import {
  getShowAllFindingTag,
  setShowAllFindingTag,
} from "utils/localStorageUtils";
import ModalUtil from "utils/modalUtil";
import { RISK_SCORE_DOC_LINK } from "utils/riskScoreUtil";
import { parseURLSearch } from "utils/urlUtil";

import ACColumnTitleWithHelpText from "ac-components/ACColumnTitleWithHelpText/ACColumnTitleWithHelpText";
import {
  ACTableColumnType,
  ColumnTitleRenderers,
} from "ac-components/ACTable/ACTable";
import { ACFilterBarItemType } from "ac-components/ac-filter-bar/ACFilterBar";

import AddToAssessmentPopup from "../Bucket/AddToAssessmentPopup";
import AddToExceptionPopup from "../Bucket/AddToExceptionPopup";
import BulkChangeStatusPopup from "../Bucket/BulkChangeStatusPopup";
import ChangeProjectsPopup from "../Bucket/ChangeProjectsPopup";
import CorrelateFindingsPopup from "../Bucket/CorrelateFindingsPopup";
import DetachTicketsPopup from "../Bucket/DetachTicketsPopup";
import { DEFAULT_HIDDEN_COLUMN_KEYS } from "../Constants";
import FindingDetailsPopup from "../FindingDetails/FindingDetailsPopup";
import { FindingModalCommonConfig } from "../FindingDetails/common";
import { useFindingFilters } from "../FindingsListV3";
import ImportFindingCSV from "../ImportFindingCSV";
import ManualFindingPopup from "../ManualFindingPopup";
import UpdateFindingFromCSV from "../UpdateFindingFromCSV";
import { tooltipContent } from "./CommonToolTipsComponent";

type Props = {
  children?: JSX.Element[] | JSX.Element;
  externalColumn: CustomFindingColumn[];
  findingCustomFields?: ArmorcodeCustomField[] | undefined;
  derivedAttributes?: DerivedAttribute[];
  hiddenColumnKeys: string[];
  overrideDefaultColumns: string[] | undefined;
  forceUpdateTimeStamp: number | null;
  isAQLv2?: boolean;
};

type FindingListContextType = {
  paginationControls: PaginationControls;
  filterColumns: ACFilterBarItemType[];
  filterValues: FilterOptionInfo;
  refreshFilters(): void;
  showManualFindingModal: () => void;
  importCSV: () => void;
  updateWithCSV: () => void;
  toggleAQL: () => void;
  showFindingDetail: (
    e: MouseEvent<HTMLAnchorElement>,
    entityNavigatorParams?: Pick<
      EntityNavigatorParams,
      "payload" | "totalEntities"
    >,
  ) => void;
  shareSearch: () => void;
  exportData: (
    type: string,
    externalColumn: CustomFindingColumn[],
    attachComments?: boolean,
    truncate?: boolean,
  ) => Promise<void>;
  externalColumn: CustomFindingColumn[];
  loading: boolean;
  dataSource: FindingSummary[];
  totalElements: number;
  isAQLActive: boolean;
  cannotSwitchToAQL: boolean;
  cannotSwitchToBasic: boolean;
  tableCustomizationHelper: TableCustomizationHelper<FindingSummary>;
  activeColumns: ExportField[];
  bulkActionPopup: (t: string) => void;
  allColumns: ACTableColumnType<FindingSummary>[];
  statusSectionRef: MutableRefObject<{ refresh: () => void }>;
  actualAppliedFilters: FilterInfo;
  columnTitleRenderers: ColumnTitleRenderers<FindingSummary>;
  findingCustomFields: ArmorcodeCustomField[] | undefined;
  totalGroupCount?: number;
  setTotalGroupCount: React.Dispatch<React.SetStateAction<number | undefined>>;
  productList: NameIdPair[];
  subProductList: NameIdPair[];
  underReviewCount: number;
  aqlSupportedFields: string[];
  aqlMetadata: AQLMetadataDTO | undefined;
  aqlConversionSupportedBasicFields: string[];
  aqlOperatorSupport: AQLOperatorSupportMap;
  basicToAQLMap: Record<string, string>;
  setShowAllTags: (flag: boolean) => void;
  showAllTags: boolean;
  hiddenColumnKeys: string[];
  isAQLv2Enabled: boolean;
  isAQLv1Active: boolean;
  isAQLv2Active: boolean;
};

const defaultValue: FindingListContextType = {
  paginationControls: DummyPaginationControlls,
  filterColumns: [],
  filterValues: {},
  refreshFilters: () => void 0,
  showManualFindingModal: () => void 0,
  importCSV: () => void 0,
  updateWithCSV: () => void 0,
  toggleAQL: () => void 0,
  showFindingDetail: () => void 0,
  shareSearch: () => void 0,
  exportData: async () => void 0,
  externalColumn: [],
  loading: true,
  dataSource: [],
  totalElements: 0,
  isAQLActive: false,
  cannotSwitchToAQL: false,
  cannotSwitchToBasic: false,
  tableCustomizationHelper: DummyCustomizationHelper,
  activeColumns: [],
  bulkActionPopup: noop,
  allColumns: [],
  statusSectionRef: { current: { refresh: noop } },
  actualAppliedFilters: {},
  columnTitleRenderers: {},
  findingCustomFields: [],
  setTotalGroupCount: noop,
  productList: [],
  subProductList: [],
  underReviewCount: 0,
  aqlSupportedFields: [],
  aqlMetadata: {
    columnMappings: [],
  },
  aqlConversionSupportedBasicFields: [],
  basicToAQLMap: {},
  aqlOperatorSupport: {
    comparisonOperatorSupportedFields: [],
    inOperatorSupportedFields: [],
    likeOperatorSupportedFields: [],
  },
  setShowAllTags: noop,
  showAllTags: false,
  hiddenColumnKeys: [],
  isAQLv2Enabled: false,
  isAQLv1Active: false,
  isAQLv2Active: false,
};

const FindingListContext =
  React.createContext<FindingListContextType>(defaultValue);

const alwaysVisibleColumnKeys = ["id"];

export default function FindingListProvider({
  children,
  externalColumn,
  findingCustomFields,
  derivedAttributes,
  hiddenColumnKeys,
  overrideDefaultColumns,
  forceUpdateTimeStamp,
  isAQLv2: isAQLv2FromProps,
}: Props) {
  const [loading, setLoading] = useState(true);
  const [loadingFindingsUnderReviewCount, setLoadingFindingsUnderReviewCount] =
    useState(true);
  const [dataSource, setDataSource] = useState<FindingSummary[]>([]);
  const [totalElements, setTotalElements] = useState(0);
  const [basicFilterResponse, setBasicFilterResponse] =
    useState<FindingFiltersRequest>();
  const [showAllTags, setShowAllTags] = useState(getShowAllFindingTag);

  const cancelRef = useRef(noop);
  const cancelAQLRef = useRef(noop);
  const statusSectionRef = useRef<{ refresh: () => void }>({ refresh: noop });
  const { globalFilterHash } = useGlobalFilterContext();
  const globalFilterHashRef = useRef(globalFilterHash);
  const featureFlags = useFeatureFlag();
  const isAQLv2Enabled = isUndefined(isAQLv2FromProps)
    ? !!featureFlags.FINDING_AQL_V2
    : isAQLv2FromProps;
  const modalRenderer = useModalRenderer();
  const entityManagerParamsRef = useRef<
    Pick<EntityNavigatorParams, "payload" | "totalEntities">
  >({ payload: {}, totalEntities: 0 });
  const [totalGroupCount, setTotalGroupCount] = useState<number>();
  const [underReviewCount, setUnderReviewCount] = useState<number>(0);
  const [isMobbSecurityConfigured, setIsMobbSecurityConfigured] =
    useState(false);

  const {
    aqlSupportedFields,
    aqlOperatorSupport,
    aqlConversionSupportedBasicFields,
    basicToAQLMap,
    aqlMetadata,
  } = useAQLFields();

  const {
    filterColumns,
    filterValues,
    refreshFilters,
    productList,
    subProductList,
  } = useFindingFilters({
    // @ts-ignore
    businessUnits: undefined,
    externalColumn,
    filters: undefined,
    findingCustomFields,
  }) as unknown as {
    filterColumns: ACFilterBarItemType[];
    filterValues: FilterOptionInfo;
    refreshFilters(): void;
    productList: NameIdPair[];
    subProductList: NameIdPair[];
  };

  const history = useHistory();
  const location = useLocation();

  const MAX_EXPORT_COUNT = 1000;

  const bucket = useFindingBucket();
  const { clear: clearSelection } = bucket;

  const paginationControls = usePaginationUrlControls({ defaultPageSize: 100 });

  const {
    page,
    pageSize,
    sortInfo,
    filterInfo,
    filterOperations,
    onFilterChange,
    resetFilters,
    groupBy,
  } = paginationControls;

  const actualAppliedFilters = useDetectFilterChange(filterInfo);

  const isBasicFilterActive =
    Object.keys(omit(actualAppliedFilters, ["aqlQuery", "aqlQueryV2"])).length >
    0;
  const isAQLv1Active = !isUndefined(filterInfo.aqlQuery);

  // if aqlQueryV2 is applied then its v2, else its v2 only if no basic filters available. When filters are empty, we need to treat it as AQL v2 if FF is enabled
  const isAQLv2Active =
    !isUndefined(filterInfo.aqlQueryV2) ||
    (isAQLv2Enabled && !isBasicFilterActive && !isAQLv1Active);
  const aqlKey = getAqlFilterKey(isAQLv2Active);

  // isAQLActive works for both v1 and v2
  const isAQLActive = isAQLv1Active || isAQLv2Active;

  const isBasicWithAQL = isAQLActive && isBasicFilterActive;

  useEffect(() => {
    setShowAllFindingTag(showAllTags);
  }, [showAllTags]);

  useEffect(() => {
    const { promise, cancel } =
      ToolsIntegrationsAPI.getMobbSecurityLoginDetails();
    promise.then((res) => {
      if (!_.isEmpty(res)) {
        setIsMobbSecurityConfigured(true);
      }
    });
    return cancel;
  }, []);

  // If a widget is using AQL and redirected to Findings, then we receive AQL with some basic filters
  // Her we are converting the remaining basic filters to AQL and appending it to the existing AQL
  // So that all the filters are visible to the users
  useEffect(() => {
    if (!isBasicWithAQL) {
      return;
    }
    const request = FindingAPI[isAQLv2Active ? "getAQLV2Query" : "getAQLQuery"](
      {
        filters: omit(actualAppliedFilters, aqlKey),
        filterOperations,
      },
    );
    cancelAQLRef.current = request.cancel;
    request.promise.then(
      ({ aqlQuery: convertedAql, aqlQueryV2: convertedAqlQueryV2 }) => {
        const aqlQuery = isAQLv2Active ? convertedAqlQueryV2 : convertedAql;
        const existingAql = actualAppliedFilters[aqlKey];
        let prefix = "";
        if (existingAql) {
          // Only wrap in parentheses if the query contains OR (needs grouping for AND precedence)
          // Avoid double-wrapping queries that are already parenthesized from the visual builder
          // @ts-expect-error - Argument of type 'FilterValue[]' is not assignable to parameter of type 'string'.ts(2345)
          const needsGrouping = /\bOR\b/i.test(existingAql);
          prefix = needsGrouping
            ? `(${existingAql}) AND `
            : `${existingAql} AND `;
        }
        onFilterChange({
          [aqlKey]: [prefix + aqlQuery],
        });
      },
    );
  }, [
    isBasicWithAQL,
    actualAppliedFilters,
    onFilterChange,
    filterOperations,
    isAQLv2Active,
    aqlKey,
    isBasicFilterActive,
  ]);

  const fetchData = useCallback(() => {
    cancelRef.current();
    setLoading(true);
    if (isBasicWithAQL) {
      return;
    }
    const sortColumns = getSortParamForAPI(sortInfo);

    //
    entityManagerParamsRef.current.payload = {
      size: pageSize,
      sortColumns,
      filters: { ...actualAppliedFilters },
      filterOperations,
      page: page - 1,
    };
    //

    const { promise, cancel } = FindingAPI.getAll({
      ...entityManagerParamsRef.current.payload,
      ticketStatusRequired: true,
    } as FindingFiltersRequestDto);
    promise
      .then((response: ListFindingResponse) => {
        entityManagerParamsRef.current.totalEntities = response.totalElements!;
        setTotalElements(response.totalElements!);
        setDataSource(response.content!);
      })
      .catch(() => {
        setDataSource([]);
      })
      .finally(() => {
        setLoading(false);
      });

    cancelRef.current = cancel;
    return cancel;
  }, [
    page,
    pageSize,
    sortInfo,
    actualAppliedFilters,
    isBasicWithAQL,
    filterOperations,
  ]);

  const fetchUnderReviewCount = useTriggerableEffect(() => {
    setLoadingFindingsUnderReviewCount(true);
    const filters = { underReview: ["true"] };
    const newParams = { filters };
    const { promise, cancel } = FindingAPI.getAllReviewed(newParams);
    promise
      .then((response) => {
        setUnderReviewCount(response?.totalElements || 0);
      })
      .catch((error) => {
        console.error("Failed to fetch under review count", error);
      })
      .finally(() => setLoadingFindingsUnderReviewCount(false));
    return cancel;
  }, [globalFilterHash]);

  const refresh = useCallback(() => {
    window.dispatchEvent(new Event("refreshList"));
    fetchData();
    fetchUnderReviewCount();
    if (statusSectionRef.current?.refresh) {
      statusSectionRef.current.refresh();
    }
  }, [fetchData, fetchUnderReviewCount]);

  const showFindingDetail = useCallback<
    FindingListContextType["showFindingDetail"]
  >(
    (e, entityNavigatorParams) => {
      if (!entityNavigatorParams) {
        entityNavigatorParams = entityManagerParamsRef.current;
      }
      const findingId = +e.currentTarget.dataset.id!;
      const index = +e.currentTarget.dataset.index!;
      modalRenderer.show({
        content: (
          <FindingDetailsPopup
            findingId={findingId}
            isMobbSecurityConfigured={isMobbSecurityConfigured}
            findingNavigatorProps={{
              api: FindingAPI.getAll,
              defaultPage:
                entityNavigatorParams.payload.size *
                  entityNavigatorParams.payload.page +
                index,
              ...entityNavigatorParams,
            }}
          />
        ),
        onClose: refresh,
        ...FindingModalCommonConfig,
      });
    },
    [modalRenderer, isMobbSecurityConfigured, refresh],
  );

  const allColumns = useFindingColumns({
    showFindingDetail,
    externalColumn,
    onFilterChange,
    filterInfo,
    isAQLActive: isAQLActive,
    findingCustomFields,
    derivedAttributes,
  });

  // Filter out admin-hidden columns at the source so they never exist downstream
  const visibleColumns = useMemo(
    () =>
      hiddenColumnKeys.length > 0
        ? allColumns.filter((c) => !hiddenColumnKeys.includes(c.key! as string))
        : allColumns,
    [allColumns, hiddenColumnKeys],
  );

  const getDefaultHiddenColumn = useMemo(() => {
    return [
      ...DEFAULT_HIDDEN_COLUMN_KEYS,
      ...map(externalColumn, (col) => `keyValue.${col.key}`),
      ...map(findingCustomFields, (col) => col.id),
      ...map(derivedAttributes, (attribute) => attribute.id),
    ];
  }, [externalColumn, findingCustomFields, derivedAttributes]);
  const tableCustomizationHelper = useTableCustomizationHelper({
    columns: visibleColumns,
    draggable: true,
    alwaysVisibleKeys: alwaysVisibleColumnKeys,
    defaultHiddenKeys: getDefaultHiddenColumn,
    prefKey: forceUpdateTimeStamp
      ? `finding_${forceUpdateTimeStamp}`
      : "finding",
    overrideDefaultColumns,
  });

  const { exportColumns } = tableCustomizationHelper;

  const cannotSwitchToAQL = !isAQLActive
    ? some(
        Object.keys(filterInfo || {}),
        (filterName) => !aqlConversionSupportedBasicFields.includes(filterName),
      )
    : false;
  const cannotSwitchToBasic =
    isAQLActive && !basicFilterResponse?.switchAllowed;

  useEffect(() => fetchData(), [fetchData]);

  useEffect(() => {
    if (globalFilterHashRef.current !== globalFilterHash) {
      globalFilterHashRef.current = globalFilterHash;
      const filterChanged = resetFilters();
      if (!filterChanged) {
        refresh();
      }
    }
  }, [globalFilterHash, resetFilters, refresh]);

  useEffect(() => {
    return () => {
      cancelRef.current();
      cancelAQLRef.current();
    };
  }, []);

  const findingsPageRegex = /\/findings/;

  const isOnFindingsPage = findingsPageRegex.test(location.pathname);

  useUpdateEffect(() => {
    if (isOnFindingsPage) {
      clearSelection();
    }
  }, [clearSelection, actualAppliedFilters, groupBy, isOnFindingsPage]);

  const showManualFindingModal = useCallback(() => {
    ModalUtil.show({
      content: <ManualFindingPopup />,
      afterClose: (created) => {
        if (created) {
          resetFilters();
        }
      },
    });
  }, [resetFilters]);
  const importCSV = useCallback(() => {
    ModalUtil.show({
      content: <ImportFindingCSV />,
      afterClose: (created) => created && resetFilters(),
    });
  }, [resetFilters]);
  const updateWithCSV = useCallback(() => {
    ModalUtil.show({
      content: <UpdateFindingFromCSV />,
      afterClose: (created) => created && resetFilters(),
    });
  }, [resetFilters]);

  useEffect(() => {
    if (!isAQLActive) {
      return;
    }
    const { promise, cancel } = FindingAPI.getBasicFiltersFromQuery(
      { filters: filterInfo },
      false,
    ); // do not show BE error, since we already throw it from /user/findings/ API
    promise.then(setBasicFilterResponse);
    return cancel;
  }, [filterInfo, isAQLActive, isBasicWithAQL, isAQLv2Enabled]);

  const toggleAQL = useCallback(() => {
    const isBasicFilter = !isAQLActive;
    if (isBasicFilter) {
      cancelAQLRef.current();
      const closeFn = message.loading("Converting to AQL");
      const request = FindingAPI.getAQLQuery({
        filters: actualAppliedFilters,
        filterOperations,
      });
      cancelAQLRef.current = request.cancel;
      request.promise.then(({ aqlQuery }) => {
        onFilterChange({ [aqlKey]: [aqlQuery] });
        closeFn();
      });
    } else {
      // hack as backend type is generated as object
      onFilterChange(
        (basicFilterResponse?.filters || {}) as unknown as FilterInfo,
        basicFilterResponse?.filterOperations,
      );
      // if(!_.isEmpty(parsedUrl.filters.slaDueDate)){
      //     parsedUrl.filters.slaDueDate = parseDueDateUIFilter(parsedUrl.filters.slaDueDate);
      // }
      // if(!_.isEmpty(parsedUrl.filters.triageDueDate)){
      //     parsedUrl.filters.triageDueDate = parseDueDateUIFilter(parsedUrl.filters.triageDueDate);
      // }
      // if(!_.isEmpty(parsedUrl.filters.remediationDueDate)){
      //     parsedUrl.filters.remediationDueDate = parseDueDateUIFilter(parsedUrl.filters.remediationDueDate);
      // }
    }
  }, [
    basicFilterResponse,
    actualAppliedFilters,
    isAQLActive,
    onFilterChange,
    filterOperations,
    aqlKey,
  ]);
  const shareSearch = useCallback(() => {
    ModalUtil.show({
      content: <SharablePopUp />,
    });
  }, []);

  const activeColumns = exportColumns;

  const exportData = useCallback(
    async (
      type: string,
      externalColumn: CustomFindingColumn[],
      attachComments: boolean = false,
      truncate: boolean = false,
    ) => {
      const { pagination } = parseURLSearch(location.search);

      const extrenalColKeys = map(
        externalColumn,
        (col) => `keyValue.${col.key}`,
      );
      const exportFields = map(activeColumns, (entry) => {
        if (extrenalColKeys.indexOf(entry.field!) !== -1) {
          entry.field = `dynamicTags.${entry.field!.split("keyValue.").pop()}`;
        }
        return entry;
      });

      const fetchComments = {
        fetchComments: attachComments,
        fetchHistory: attachComments,
        fetchAttachments: attachComments,
        truncateIfExceedsExcelLimit: truncate,
      };

      try {
        const commonPayload = {
          ...omit(pagination, "sort"),
          sortColumns: getSortParamForAPI(sortInfo),
          filters: actualAppliedFilters,
          filterOperations,
          exportFields,
          ...fetchComments,
        };
        const updatePayloadForCsvSelected = (
          payload: typeof commonPayload,
          csvSelected: boolean,
        ) => {
          if (csvSelected) {
            return {
              ...payload,
              filters: {
                id: bucket.findingIds,
              },
            };
          }
          return payload;
        };
        const proceedWithExport = async (csvSelected: boolean) => {
          const closeFn = message.loading("Preparing CSV");
          const payload = updatePayloadForCsvSelected(
            commonPayload,
            csvSelected,
          );
          const data = await FindingAPI.downloadCsvV2(payload).promise;
          closeFn();
          const { presignedUrl: url, processingInBackground } = data;
          if (processingInBackground) {
            message.info("CSV link will be emailed once ready.");
          } else {
            window.open(url, "_blank", "noopener");
          }
        };
        if (type === "csv" || type === "csvSelected") {
          if (
            type === "csvSelected"
              ? bucket.findingIds.length > MAX_EXPORT_COUNT
              : totalElements > MAX_EXPORT_COUNT
          ) {
            ModalUtil.show({
              content: (
                <WarningPopup
                  //@ts-expect-error TODO Fix type
                  promptMessage={
                    <>
                      Your export contains more than 1,000 rows. We will prepare
                      the full CSV file and send you an email with a download
                      link once it's ready. This may take some time.
                      <br />
                      To receive a smaller file immediately, you can apply
                      filters to reduce the number of findings.
                      <br />
                      <br />
                      <b>Note:</b> For exports exceeding 1,000 rows, data will
                      be unsorted to ensure a complete export.
                    </>
                  }
                  title="Export Findings to CSV"
                  okText="Proceed with Export"
                  onOk={() =>
                    proceedWithExport(type === "csvSelected" ? true : false)
                  }
                  onCancel={noop}
                />
              ),
            });
          } else {
            proceedWithExport(type === "csvSelected" ? true : false);
          }
        } else {
          FindingAPI.downloadPdf({
            ...commonPayload,
            ...(type === "detailPdf" ? { fetchDetails: true } : {}),
          });
          message.info(
            type === "detailPdf"
              ? "We'll email you the link to download the .zip file once it's ready. If findings exceeds 1K we will split pdf into multiple files."
              : "PDF link will be emailed once ready.",
          );
        }
      } catch (e) {
        console.error(e);
      }
    },
    [
      location.search,
      activeColumns,
      sortInfo,
      actualAppliedFilters,
      bucket.findingIds,
      totalElements,
      filterOperations,
    ],
  );

  const bulkActionPopup = useCallback(
    (type: string) => {
      let Component: React.FC<any>;
      let props = {};
      let shouldRefreshFilters = false;
      switch (type) {
        case "propose-status":
          Component = BulkChangeStatusPopup;
          props = {
            isPropose: true,
          };
          break;
        case "change-status":
          Component = BulkChangeStatusPopup;
          break;
        case "detach-tickets":
          Component = DetachTicketsPopup;
          break;
        case "correlate":
          Component = CorrelateFindingsPopup;
          break;
        case "add-exception":
          shouldRefreshFilters = true;
          Component = AddToExceptionPopup;
          break;
        case "add-assessment":
          shouldRefreshFilters = true;
          Component = AddToAssessmentPopup;
          break;
        case "add-project":
          shouldRefreshFilters = true;
          Component = ChangeProjectsPopup;
          break;
        default:
          console.log(`Unkown action (${type})`);
          return;
      }
      if (Component) {
        ModalUtil.show({
          content: (
            <Component
              afterClose={noop}
              {...props}
              bucket={bucket}
              history={history}
            />
          ),
          afterClose: (shouldRefresh) => {
            if (shouldRefresh) {
              fetchData();
            }
            if (shouldRefreshFilters) {
              refreshFilters();
            }
            fetchUnderReviewCount();
          },
        });
      }
    },
    [bucket, history, fetchData, refreshFilters, fetchUnderReviewCount],
  );

  const columnTitleRenderers = useMemo<ColumnTitleRenderers<FindingSummary>>(
    () => ({
      findingScore: (column) => (
        <ACColumnTitleWithHelpText
          title={column.title}
          tooltipContent={
            featureFlags.RISK_SCORE_3_0 ? (
              <>
                <Vocabulary name="findingScore" /> (0-100): the technical risk
                of the Vulnerability or Weakness associated with the Finding{" "}
                {RISK_SCORE_DOC_LINK}
              </>
            ) : (
              <>
                The <Vocabulary name="findingScore" /> is a metric used to
                assess the risk level of an individual finding. It is calculated
                based on several parameters, including Severity Risk, Tool
                Weightage, Base Score, and AATI Score. The algorithm used to
                calculate the <Vocabulary name="findingScore" /> takes all these
                factors into account.{" "}
                <Typography.Link
                  style={{ color: "white" }}
                  onClick={(e) => e.stopPropagation()}
                  href="/api/jwt/zendesk/sso?return_to=https://support.armorcode.com/hc/en-us/articles/19334789998355-Findings#h_01H9HZJCK186FEX6EG0NS7TT4T"
                >
                  Read more
                </Typography.Link>
              </>
            )
          }
        />
      ),
      aatiScore: (column) => (
        <ACColumnTitleWithHelpText
          title={column.title}
          tooltipContent={tooltipContent["aatiScore"]}
        />
      ),
      baseScore: (column) => (
        <ACColumnTitleWithHelpText
          title={column.title}
          tooltipContent={tooltipContent["baseScore"]}
        />
      ),
      epssScore: (column) => (
        <ACColumnTitleWithHelpText
          title={column.title}
          tooltipContent={tooltipContent["epssScore"]}
        />
      ),
      epssProbability: (column) => (
        <ACColumnTitleWithHelpText
          title={column.title}
          tooltipContent={tooltipContent["epssScore"]}
        />
      ),
      ruleId: (column) => (
        <ACColumnTitleWithHelpText
          title={column.title}
          tooltipContent="ArmorCode rule x.y.z where x is the main category, y is the sub-category and z is the rule id inside this category/sub-category."
        />
      ),
      assetScore: (column) => (
        <ACColumnTitleWithHelpText
          title={column.title}
          tooltipContent={
            <>
              <Vocabulary name="assetScore" /> (1-10): derived based on tags
              used to score the business impact of the asset being compromised.{" "}
              {RISK_SCORE_DOC_LINK}
            </>
          }
        />
      ),
    }),
    [featureFlags.RISK_SCORE_3_0],
  );

  const value = useMemo<FindingListContextType>(() => {
    return {
      paginationControls,
      filterColumns,
      filterValues,
      refreshFilters,
      showManualFindingModal,
      importCSV,
      updateWithCSV,
      showFindingDetail,
      shareSearch,
      exportData,
      externalColumn,
      loading: loading || loadingFindingsUnderReviewCount,
      dataSource,
      totalElements,
      toggleAQL,
      isAQLActive,
      cannotSwitchToAQL,
      cannotSwitchToBasic,
      tableCustomizationHelper,
      activeColumns,
      bulkActionPopup,
      allColumns: visibleColumns,
      statusSectionRef,
      actualAppliedFilters,
      columnTitleRenderers,
      findingCustomFields,
      derivedAttributes,
      totalGroupCount,
      setTotalGroupCount,
      productList,
      subProductList,
      underReviewCount,
      aqlSupportedFields,
      aqlMetadata,
      aqlConversionSupportedBasicFields,
      basicToAQLMap,
      aqlOperatorSupport,
      setShowAllTags,
      showAllTags,
      hiddenColumnKeys,
      isAQLv2Enabled,
      isAQLv1Active,
      isAQLv2Active,
    };
  }, [
    paginationControls,
    filterColumns,
    filterValues,
    refreshFilters,
    showManualFindingModal,
    importCSV,
    updateWithCSV,
    showFindingDetail,
    shareSearch,
    exportData,
    externalColumn,
    loading,
    loadingFindingsUnderReviewCount,
    dataSource,
    totalElements,
    toggleAQL,
    isAQLActive,
    activeColumns,
    cannotSwitchToAQL,
    cannotSwitchToBasic,
    bulkActionPopup,
    visibleColumns,
    statusSectionRef,
    actualAppliedFilters,
    columnTitleRenderers,
    tableCustomizationHelper,
    findingCustomFields,
    derivedAttributes,
    totalGroupCount,
    setTotalGroupCount,
    productList,
    subProductList,
    underReviewCount,
    aqlOperatorSupport,
    aqlConversionSupportedBasicFields,
    basicToAQLMap,
    aqlSupportedFields,
    aqlMetadata,
    setShowAllTags,
    showAllTags,
    hiddenColumnKeys,
    isAQLv2Enabled,
    isAQLv1Active,
    isAQLv2Active,
  ]);

  if (isBasicWithAQL) {
    return <AppLoader />;
  }

  return (
    <FindingListContext.Provider value={value}>
      {children}
      {modalRenderer.context}
    </FindingListContext.Provider>
  );
}

export const useFindingListContext = () => {
  return useContext(FindingListContext);
};

export const getSortParamForAPI = (sortInfo: SortInfo) => {
  const sortColumns: FindingListSortOrder[] = [];
  forEach(sortInfo, (order, key) => {
    sortColumns.push({
      property: key,
      direction: order?.replace("end", ""),
    });
  });
  return sortColumns;
};
